import React, { useState } from 'react'
import { ThumbsUp, ThumbsDown, MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FeedbackModal } from './FeedbackModal'
import { FEEDBACK_RATINGS } from '@shared/schemas/feedback'
import { cn } from '@/lib/utils'

interface FeedbackButtonProps {
  messageId: string
  currentFeedback?: {
    rating: number
    comment: string | null
  } | null
  onSubmitFeedback: (rating: number, comment?: string) => Promise<void>
  disabled?: boolean
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export const FeedbackButton: React.FC<FeedbackButtonProps> = ({
  messageId,
  currentFeedback,
  onSubmitFeedback,
  disabled = false,
  size = 'sm',
  className
}) => {
  const [showModal, setShowModal] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [pendingRating, setPendingRating] = useState<number | null>(null)

  // Determine current state
  const currentRating = currentFeedback?.rating || null
  const hasThumbsUp = currentRating === FEEDBACK_RATINGS.THUMBS_UP
  const hasThumbsDown = currentRating === FEEDBACK_RATINGS.THUMBS_DOWN
  const hasComment = currentFeedback?.comment && currentFeedback.comment.length > 0

  const handleQuickFeedback = async (rating: number) => {
    if (disabled || submitting) return
    
    setSubmitting(true)
    setPendingRating(rating)
    
    try {
      await onSubmitFeedback(rating)
    } catch (error) {
      console.error('Failed to submit feedback:', error)
    } finally {
      setSubmitting(false)
      setPendingRating(null)
    }
  }

  const handleFeedbackWithComment = async (rating: number, comment?: string) => {
    if (disabled || submitting) return
    
    setSubmitting(true)
    
    try {
      await onSubmitFeedback(rating, comment)
      setShowModal(false)
    } catch (error) {
      console.error('Failed to submit feedback:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const openCommentModal = () => {
    if (disabled) return
    setShowModal(true)
  }

  const buttonSize = {
    sm: 'h-7 w-7',
    md: 'h-8 w-8', 
    lg: 'h-10 w-10'
  }

  const iconSize = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  }

  return (
    <>
      <div className={cn('flex items-center gap-1', className)}>
        {/* Thumbs Up Button */}
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            buttonSize[size],
            'p-0 transition-all duration-200',
            hasThumbsUp
              ? 'text-green-600 bg-green-50 hover:bg-green-100 dark:text-green-400 dark:bg-green-900/20'
              : 'text-muted-foreground hover:text-green-600 hover:bg-green-50 dark:hover:text-green-400 dark:hover:bg-green-900/20',
            disabled && 'opacity-50 cursor-not-allowed',
            submitting && pendingRating === FEEDBACK_RATINGS.THUMBS_UP && 'animate-pulse'
          )}
          onClick={() => handleQuickFeedback(FEEDBACK_RATINGS.THUMBS_UP)}
          disabled={disabled || submitting}
          title={hasThumbsUp ? 'You liked this response' : 'Like this response'}
        >
          <ThumbsUp 
            className={cn(
              iconSize[size],
              hasThumbsUp && 'fill-current'
            )} 
          />
        </Button>

        {/* Thumbs Down Button */}
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            buttonSize[size],
            'p-0 transition-all duration-200',
            hasThumbsDown
              ? 'text-red-600 bg-red-50 hover:bg-red-100 dark:text-red-400 dark:bg-red-900/20'
              : 'text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/20',
            disabled && 'opacity-50 cursor-not-allowed',
            submitting && pendingRating === FEEDBACK_RATINGS.THUMBS_DOWN && 'animate-pulse'
          )}
          onClick={() => handleQuickFeedback(FEEDBACK_RATINGS.THUMBS_DOWN)}
          disabled={disabled || submitting}
          title={hasThumbsDown ? 'You disliked this response' : 'Dislike this response'}
        >
          <ThumbsDown 
            className={cn(
              iconSize[size],
              hasThumbsDown && 'fill-current'
            )} 
          />
        </Button>

        {/* Comment Button */}
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            buttonSize[size],
            'p-0 transition-all duration-200',
            hasComment
              ? 'text-blue-600 bg-blue-50 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20'
              : 'text-muted-foreground hover:text-blue-600 hover:bg-blue-50 dark:hover:text-blue-400 dark:hover:bg-blue-900/20',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          onClick={openCommentModal}
          disabled={disabled}
          title={hasComment ? 'Edit feedback comment' : 'Add feedback comment'}
        >
          <MessageSquare className={iconSize[size]} />
        </Button>
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        messageId={messageId}
        currentFeedback={currentFeedback}
        onSubmit={handleFeedbackWithComment}
        submitting={submitting}
      />
    </>
  )
}

export default FeedbackButton